
import React, { useCallback, useState } from "react";
import ShoppingListItem from "./ShoppingListItem";
import ShoppingListRow from "./ShoppingListRow";
import { ShoppingItemWithProduct, CategoryInfo } from "@/types";
import { useCategoryOrdering, useShops } from '@/hooks/useShops';
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { ChevronDown, ChevronUp } from "lucide-react";

interface CategoryListViewProps {
  listId: string;
  items: ShoppingItemWithProduct[];
  // Le categorie vengono passate già ordinate
  categories: CategoryInfo[];
  onCheckItem: (id: string, checked: boolean) => void;
  onDeleteItem: (id: string) => void;
  onEditItem: (item: ShoppingItemWithProduct) => void;
  rapidInsert: boolean;
  shopId?: string;
  viewMode: 'grid' | 'list';
}

const CategoryListView = ({
  listId,
  items,
  categories,
  onCheckItem,
  onDeleteItem,
  onEditItem,
  rapidInsert,
  shopId,
  viewMode
}: CategoryListViewProps) => {
  const { theme } = useThemeConfig();
  // Recupera i negozi per mostrare il nome della categoria nascosta
  const { shops } = useShops();
  const shopName = shops.find((s) => s.id === shopId)?.shop_name;

  const handleToggleItem = useCallback(
    (item: ShoppingItemWithProduct) => {
      onCheckItem(item.id, !item.checked);
    },
    [onCheckItem]
  );

  const handleEdit = useCallback(
    (item: ShoppingItemWithProduct) => {
      onEditItem(item);
    },
    [onEditItem]
  );
  // Offline fallback: if categories are empty we create a single default one
  // so that lists still render correctly without network data
  const { data: shopOrdering } = useCategoryOrdering(shopId || null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set()
  );

  const collapseCategory = useCallback((categoryId: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      next.add(categoryId);
      return next;
    });
  }, []);

  const expandCategory = useCallback((categoryId: string) => {
    setCollapsedCategories((prev) => {
      if (!prev.has(categoryId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(categoryId);
      return next;
    });
  }, []);

  const categoriesToRenderBase: CategoryInfo[] =
    categories.length > 0
      ? categories
      : [{
          id: 'none',
          name: 'Altri articoli',
          color: '#f0f0f0',
          icon: '📋',
          position: 0,
          textColor: '#000000'
        }];

  const hiddenCategories = new Set(
    (shopOrdering ?? []).filter((e) => e.visibile === false).map((e) => e.category_id)
  );

  let categoriesToRender = categoriesToRenderBase.filter(
    (cat) => !hiddenCategories.has(cat.id)
  );

  // Raggruppa gli articoli per categoria
  const groupedItems: Record<string, ShoppingItemWithProduct[]> = {};

  // Ensure all categories have an entry in groupedItems
  categoriesToRender.forEach(category => {
    groupedItems[category.id] = [];
  });
  
  // Aggiungi una categoria per gli elementi senza categoria
  // Se un articolo appartiene a una categoria rimossa, verrà inserito qui
  if (!groupedItems['none']) {
    groupedItems['none'] = [];
  }
  
  let hasHiddenForShop = false;

  // Add items to their respective categories
  items.forEach(item => {
    if (hiddenCategories.has(item.category)) {
      hasHiddenForShop = true;
      groupedItems['none'].push(item);
      return;
    }
    const matchingCategory = categories.find(
      cat => (cat.categoryId ?? cat.id) === item.category
    );
    
    if (matchingCategory) {
      // Se troviamo una categoria corrispondente, aggiungiamo l'articolo a quella categoria
      if (!groupedItems[matchingCategory.id]) {
        groupedItems[matchingCategory.id] = [];
      }
      groupedItems[matchingCategory.id].push(item);
    } else {
      // Se non troviamo una categoria corrispondente, aggiungiamo l'articolo alla categoria 'none'
      groupedItems['none'].push(item);
    }
  });

  // Categoria fittizia per articoli appartenenti a categorie nascoste
  const missingCategory: CategoryInfo = {
    id: 'none',
    name: hasHiddenForShop && shopName ? `Categoria nascosta per ${shopName}` : 'Categoria nascosta',
    color: '#f0f0f0',
    icon: '❓',
    position: 999,
    textColor: '#000',
  };

  // Se esistono elementi senza categoria visibile, aggiungiamo una categoria fittizia
  if (
    groupedItems['none'].length > 0 &&
    !categoriesToRender.some((c) => c.id === 'none')
  ) {
    categoriesToRender.push(missingCategory);
  }

  // Crea mappa category_id -> position per ordinamento negozio
  const orderingMap = new Map(
    (shopOrdering ?? []).map((entry) => [entry.category_id, entry.position])
  );

  // ✅ FIX: Risolto errore aritmetico usando Number() per conversion sicura
  const sortedCategories = [...categoriesToRender]
    .map((cat) => ({
      ...cat,
      position: orderingMap.has(cat.id)
        ? Number(orderingMap.get(cat.id))
        : Number(cat.position ?? Number.MAX_SAFE_INTEGER)
    }))
    .sort((a, b) => Number(a.position) - Number(b.position));

  return (
    <div className={`space-y-6 ${viewMode === 'list' ? 'pl-[8px]' : ''}`}>
      {sortedCategories.map((category) => {
        const categoryItems = groupedItems[category.id] || [];
        const hasItems = categoryItems.length > 0;
        const isCollapsed = collapsedCategories.has(category.id);

        if (!hasItems) return null;

        return (
          <div
            key={category.id}
            className={`rounded-lg shadow-sm overflow-hidden border ${viewMode === 'list' ? 'w-[98%]' : ''}`}
            style={{
              backgroundColor: theme.cardBackgroundColor,
              borderColor: theme.borderColor
            }}
          >
            <div
              className="px-4 py-1 font-medium flex items-center"
              style={{
                backgroundColor: category.color || "#f0f0f0",
                color: category.textColor || "#000000",
              }}
            >
              <span className="mr-2">{category.icon || "📋"}</span>
              {category.name || "Altri articoli"}
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => collapseCategory(category.id)}
                  disabled={isCollapsed}
                  className="rounded p-1 transition-colors disabled:opacity-50"
                  style={{ color: category.textColor || "#000000" }}
                  aria-label={`Comprimi categoria ${category.name || 'Altri articoli'}`}
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => expandCategory(category.id)}
                  disabled={!isCollapsed}
                  className="rounded p-1 transition-colors disabled:opacity-50"
                  style={{ color: category.textColor || "#000000" }}
                  aria-label={`Espandi categoria ${category.name || 'Altri articoli'}`}
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            </div>
            {!isCollapsed && viewMode === 'grid' ? (
              <div className="grid grid-cols-3 gap-2 p-3">
                {categoryItems.map((item) => (
                  <ShoppingListItem
                    key={item.id}
                    item={item}
                    onToggle={handleToggleItem}
                    onCheck={onCheckItem}
                    onDelete={onDeleteItem}
                    onEdit={handleEdit}
                    rapidInsert={rapidInsert}
                  />
                ))}
              </div>
            ) : null}
            {!isCollapsed && viewMode === 'list' ? (
              <ul className="list-disc pl-5 p-3 space-y-0">
                {categoryItems.map((item) => (
                  <ShoppingListRow
                    key={item.id}
                    item={item}
                    onToggle={handleToggleItem}
                    onCheck={onCheckItem}
                    onEdit={handleEdit}
                    rapidInsert={rapidInsert}
                  />
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(CategoryListView);
